import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Download,
  MapPin,
  User,
  Video,
  Mic,
  Image,
  FileText,
  Trash2,
  AlertTriangle,
} from 'lucide-react';
import { getSession, deleteSession } from '../services/api';
import { useCurrentUser } from '../context/UserContext';
import { format } from 'date-fns';

interface MediaFile {
  filename: string;
  sha256: string;
  sizeBytes: number;
  durationSeconds?: number;
  caption?: string;
}

interface SessionDetail {
  sessionId: string;
  projectId: string;
  enumeratorId: string;
  enumeratorName: string;
  status: string;
  language: string;
  dialect: string;
  recordingEnvironment?: string;
  duration?: number;
  location: {
    state: string;
    district: string;
    block: string;
    village: string;
    gps: { lat: number; lng: number; accuracy: number };
  };
  respondent?: {
    name: string;
    phone: string;
    age: number;
    gender: string;
    primaryLanguage: string;
    secondaryLanguages: string[];
    educationLevel: string;
    occupation: string;
    monthlyIncomeBracket: string;
    socioeconomicClass: string;
    urbanTier: string;
    familySize: number;
    lifeSatisfactionScore: number;
    consentGiven: boolean;
    notes?: string;
  };
  media?: {
    video: MediaFile;
    audio: MediaFile;
    images: MediaFile[];
  };
  downloadUrls?: Record<string, string>;
  createdAt: string;
  completedAt?: string;
  s3Prefix?: string;
}

function InfoRow({ label, value }: { label: string; value: string | number | undefined }) {
  if (!value && value !== 0) return null;
  return (
    <div className="flex gap-3">
      <span className="text-sm text-gray-500 w-40 flex-shrink-0">{label}</span>
      <span className="text-sm text-gray-900 font-medium">{value}</span>
    </div>
  );
}

function formatSize(bytes?: number) {
  if (!bytes) return '—';
  if (bytes > 1073741824) return `${(bytes / 1073741824).toFixed(1)} GB`;
  if (bytes > 1048576) return `${(bytes / 1048576).toFixed(1)} MB`;
  return `${(bytes / 1024).toFixed(0)} KB`;
}

function DeleteConfirmModal({
  onConfirm,
  onCancel,
  isLoading,
}: {
  onConfirm: () => void;
  onCancel: () => void;
  isLoading: boolean;
}) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-red-100 rounded-full">
            <AlertTriangle size={20} className="text-red-600" />
          </div>
          <h2 className="text-lg font-bold text-gray-900">Delete this session?</h2>
        </div>
        <p className="text-sm text-gray-500 mb-6">
          This will permanently delete the session record and all associated media files (video,
          audio, images) from S3. <strong>This action cannot be undone.</strong>
        </p>
        <div className="flex gap-3 justify-end">
          <button className="btn-secondary" onClick={onCancel} disabled={isLoading}>
            Cancel
          </button>
          <button
            className="px-4 py-2 bg-red-600 text-white text-sm font-semibold rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
            onClick={onConfirm}
            disabled={isLoading}
          >
            {isLoading && (
              <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            )}
            Delete Permanently
          </button>
        </div>
      </div>
    </div>
  );
}

export default function SessionDetailPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isAdmin } = useCurrentUser();
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const { data: session, isLoading } = useQuery<SessionDetail>({
    queryKey: ['session', sessionId],
    queryFn: () => getSession(sessionId!, true),
    enabled: !!sessionId,
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteSession(sessionId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sessions'] });
      navigate('/sessions');
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin h-8 w-8 border-4 border-brand-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!session) {
    return (
      <div className="card text-center py-12">
        <p className="text-gray-500">Session not found</p>
        <button className="btn-secondary mt-4" onClick={() => navigate(-1)}>
          Go Back
        </button>
      </div>
    );
  }

  const formatDuration = (s?: number) => {
    if (!s) return '—';
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${String(sec).padStart(2, '0')}`;
  };

  // Collect all download URLs for the "Download All" button
  const allDownloads: { href: string; filename: string }[] = [];
  if (session.downloadUrls?.video) allDownloads.push({ href: session.downloadUrls.video, filename: 'video.mp4' });
  if (session.downloadUrls?.audio) allDownloads.push({ href: session.downloadUrls.audio, filename: 'audio.wav' });
  if (session.downloadUrls?.metadata) allDownloads.push({ href: session.downloadUrls.metadata, filename: 'metadata.json' });
  session.media?.images?.forEach((img, i) => {
    if (session.downloadUrls?.[`image_${i}`]) {
      allDownloads.push({ href: session.downloadUrls[`image_${i}`], filename: img.filename });
    }
  });

  const handleDownloadAll = () => {
    allDownloads.forEach(({ href, filename }) => {
      const a = document.createElement('a');
      a.href = href;
      a.download = filename;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate(-1)}
          className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
        >
          <ArrowLeft size={18} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-bold text-gray-900 truncate">
              Session: {session.sessionId.substring(0, 12)}…
            </h1>
            <span
              className={`badge ${session.status === 'completed' ? 'badge-green' : 'badge-yellow'}`}
            >
              {session.status}
            </span>
          </div>
          <p className="text-sm text-gray-500 mt-0.5">
            {format(new Date(session.createdAt), 'dd MMM yyyy, HH:mm')}
          </p>
        </div>

        {/* Admin actions */}
        {isAdmin && (
          <div className="flex items-center gap-2 flex-shrink-0">
            {allDownloads.length > 0 && (
              <button
                onClick={handleDownloadAll}
                className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-brand-700 bg-brand-50 border border-brand-200 rounded-lg hover:bg-brand-100"
                title={`Download all ${allDownloads.length} files`}
              >
                <Download size={14} />
                Download All
              </button>
            )}
            <button
              onClick={() => setShowDeleteModal(true)}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100"
            >
              <Trash2 size={14} />
              Delete
            </button>
          </div>
        )}
      </div>

      {session.s3Prefix && (
        <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg">
          <p className="text-xs text-gray-400 mb-0.5">S3 path</p>
          <code className="text-xs text-gray-600">{session.s3Prefix}/</code>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Session Info */}
        <div className="card space-y-3">
          <h2 className="font-semibold text-gray-800 flex items-center gap-2">
            <Video size={16} />
            Recording Details
          </h2>
          <InfoRow label="Duration" value={formatDuration(session.duration)} />
          <InfoRow label="Language" value={session.language} />
          <InfoRow label="Dialect" value={session.dialect} />
          <InfoRow label="Environment" value={session.recordingEnvironment} />
          <InfoRow label="Enumerator" value={session.enumeratorName} />
          <InfoRow
            label="Completed"
            value={
              session.completedAt
                ? format(new Date(session.completedAt), 'dd MMM yyyy HH:mm')
                : undefined
            }
          />
        </div>

        {/* Location */}
        <div className="card space-y-3">
          <h2 className="font-semibold text-gray-800 flex items-center gap-2">
            <MapPin size={16} />
            Location
          </h2>
          <InfoRow label="State" value={session.location?.state} />
          <InfoRow label="District" value={session.location?.district} />
          <InfoRow label="Block" value={session.location?.block} />
          <InfoRow label="Village" value={session.location?.village} />
          {session.location?.gps && (
            <InfoRow
              label="GPS"
              value={`${session.location.gps.lat.toFixed(5)}, ${session.location.gps.lng.toFixed(5)} (±${session.location.gps.accuracy}m)`}
            />
          )}
        </div>

        {/* Respondent */}
        {session.respondent && (
          <div className="card space-y-3">
            <h2 className="font-semibold text-gray-800 flex items-center gap-2">
              <User size={16} />
              Respondent Metadata
            </h2>
            <InfoRow label="Name" value={session.respondent.name} />
            <InfoRow label="Age" value={session.respondent.age} />
            <InfoRow label="Gender" value={session.respondent.gender} />
            <InfoRow label="Primary Language" value={session.respondent.primaryLanguage} />
            <InfoRow label="Education" value={session.respondent.educationLevel} />
            <InfoRow label="Occupation" value={session.respondent.occupation} />
            <InfoRow label="Income Bracket" value={session.respondent.monthlyIncomeBracket} />
            <InfoRow label="SEC Class" value={session.respondent.socioeconomicClass} />
            <InfoRow label="Urban Tier" value={session.respondent.urbanTier} />
            <InfoRow label="Family Size" value={session.respondent.familySize} />
            <InfoRow
              label="Life Satisfaction"
              value={`${session.respondent.lifeSatisfactionScore} / 10`}
            />
            <InfoRow label="Consent" value={session.respondent.consentGiven ? 'Yes' : 'No'} />
          </div>
        )}

        {/* Media Files */}
        {session.media && (
          <div className="card space-y-4">
            <h2 className="font-semibold text-gray-800 flex items-center gap-2">
              <FileText size={16} />
              Media Files
            </h2>

            {/* Video */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                <Video size={18} className="text-brand-500" />
                <div>
                  <p className="text-sm font-medium">video.mp4</p>
                  <p className="text-xs text-gray-400">{formatSize(session.media.video?.sizeBytes)}</p>
                </div>
              </div>
              {session.downloadUrls?.video && (
                <a
                  href={session.downloadUrls.video}
                  download="video.mp4"
                  className="text-brand-600 hover:text-brand-800"
                >
                  <Download size={16} />
                </a>
              )}
            </div>

            {/* Audio */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                <Mic size={18} className="text-accent-500" />
                <div>
                  <p className="text-sm font-medium">audio.wav</p>
                  <p className="text-xs text-gray-400">{formatSize(session.media.audio?.sizeBytes)}</p>
                </div>
              </div>
              {session.downloadUrls?.audio && (
                <a
                  href={session.downloadUrls.audio}
                  download="audio.wav"
                  className="text-brand-600 hover:text-brand-800"
                >
                  <Download size={16} />
                </a>
              )}
            </div>

            {/* Images */}
            {session.media.images?.map((img, i) => (
              <div key={img.filename} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Image size={18} className="text-emerald-500" />
                  <div>
                    <p className="text-sm font-medium">{img.filename}</p>
                    <p className="text-xs text-gray-400">
                      {img.caption ?? ''} • {formatSize(img.sizeBytes)}
                    </p>
                  </div>
                </div>
                {session.downloadUrls?.[`image_${i}`] && (
                  <a
                    href={session.downloadUrls[`image_${i}`]}
                    download={img.filename}
                    className="text-brand-600 hover:text-brand-800"
                  >
                    <Download size={16} />
                  </a>
                )}
              </div>
            ))}

            {/* Metadata JSON */}
            {session.downloadUrls?.metadata && (
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <FileText size={18} className="text-gray-500" />
                  <div>
                    <p className="text-sm font-medium">metadata.json</p>
                    <p className="text-xs text-gray-400">Session metadata</p>
                  </div>
                </div>
                <a
                  href={session.downloadUrls.metadata}
                  download="metadata.json"
                  className="text-brand-600 hover:text-brand-800"
                >
                  <Download size={16} />
                </a>
              </div>
            )}
          </div>
        )}
      </div>

      {showDeleteModal && (
        <DeleteConfirmModal
          onConfirm={() => deleteMutation.mutate()}
          onCancel={() => setShowDeleteModal(false)}
          isLoading={deleteMutation.isPending}
        />
      )}
    </div>
  );
}
